import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import DiscussionCard from './components/DiscussionCard';
import Pagination from '../../components/ui/Pagination';
import articlesService from '../../services/articlesService';
import engagementService from '../../services/engagementService';
import { useAuth } from '../../contexts/AuthContext';

const DiscussionsFeed = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [activeCircle, setActiveCircle] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const topRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchBookmarkIds = async () => {
      try {
        const { data } = await articlesService.getMyBookmarkIds();
        setBookmarkedIds(new Set(data));
      } catch (err) {
        console.error('Failed to fetch bookmark IDs:', err);
      }
    };
    fetchBookmarkIds();
  }, [isAuthenticated]);

  const handleBookmarkToggle = async (articleId) => {
    if (!isAuthenticated) return;
    try {
      const wasBookmarked = bookmarkedIds.has(articleId);
      if (wasBookmarked) {
        await engagementService.removeBookmark(articleId);
        setBookmarkedIds((prev) => {
          const next = new Set(prev);
          next.delete(articleId);
          return next;
        });
      } else {
        await engagementService.bookmarkArticle(articleId);
        setBookmarkedIds((prev) => new Set(prev).add(articleId));
      }
    } catch (err) {
      console.error('Bookmark toggle failed:', err);
    }
  };

  const ITEMS_PER_PAGE = 12;

  useEffect(() => {
    const fetchDiscussions = async () => {
      setLoading(true);
      try {
        console.log('Fetching discussions for circle:', activeCircle);
        // Explicitly handle 'all' by passing undefined for circleSlug
        const circleSlug = activeCircle !== 'all' ? activeCircle : undefined;

        const { data } = await articlesService.getArticles({
          type: 'Discussion',
          page: currentPage,
          pageSize: ITEMS_PER_PAGE,
          circleSlug
        });

        console.log('Discussions data fetched:', data);
        setDiscussions(data.items || []);
        setTotalCount(data.totalCount || 0);
      } catch (error) {
        console.error('Failed to fetch discussions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDiscussions();
  }, [currentPage, activeCircle]);

  const circles = [
    {
      id: 'literature',
      name: 'دائرة الأدب',
      icon: 'BookOpen',
      color: '#8B6914',
      subtext: 'أدب عربي - أدب مقارن',
      hasChildren: true
    },
    {
      id: 'soul',
      name: 'دائرة النفس',
      icon: 'Heart',
      color: '#7D8471',
      subtext: 'رحلة في أعماق الذات'
    },
    {
      id: 'visuals',
      name: 'دائرة العين',
      icon: 'Eye',
      color: '#D4AF37',
      subtext: 'لأن الرؤية تسبق كل شيء.'
    },
    {
      id: 'sound',
      name: 'دائرة الصوت',
      icon: 'Music',
      color: '#8B7355',
      subtext: 'قسمٌ معني بالصوتيات، كالموسيقى والغناء'
    },
    {
      id: 'philosophy',
      name: 'دائرة العقل',
      icon: 'Brain',
      color: '#6B5B47',
      subtext: 'قسمٌ معنيٌ بالفلسفة. لأن الفلسفة هي الحكمة...'
    },
    {
      id: 'heritage',
      name: 'دائرة التراث',
      icon: 'Landmark',
      color: '#8B4513',
      subtext: 'تأملات لأصحابها'
    },
    {
      id: 'arts',
      name: 'دائرة الفنون',
      icon: 'Palette',
      color: '#A0826D',
      subtext: 'قسمٌ معنيٌ بالفنون البصرية والتشكيلية'
    },
    {
      id: 'language',
      name: 'دائرة اللسان',
      icon: 'MessageSquare',
      color: '#6B8E23',
      subtext: 'قسمٌ معنيٌ باللغة العربية وتراكيبها.'
    },
    {
      id: 'reflections',
      name: 'دائرة الرحلة',
      icon: 'Sparkles',
      color: '#9B7653',
      subtext: 'تأملات شخصية وخواطر روحية'
    },
    {
      id: 'sufism',
      name: 'دائرة الدائرة',
      icon: 'Star',
      color: '#5A5A5A',
      subtext: 'افتح عينك؛ فما ثم إلا وجودٌ مستمر...'
    }
  ];

  const handleFilterChange = (circleId, e) => {
    if (e) {
      e?.preventDefault();
    }
    if (activeCircle === circleId) {
      setActiveCircle('all');
    } else {
      setActiveCircle(circleId);
    }
    setCurrentPage(1);
  };

  // Safe mapping of backend data to component props
  const currentItems = Array.isArray(discussions) ? discussions.map(article => {
    if (!article) return null;
    return {
      id: article.id,
      category: article.circle, // "دائرة اللسان" e.g.
      categoryEn: article.circleEn,
      question: article.title,
      questionEn: article.titleEn,
      initiator: {
        name: article.author || 'Unknown',
        avatar: article.authorImage || null, // Ensure null if empty
        avatarAlt: article.authorImageAlt || 'User Avatar'
      },
      participants: article.claps ?? 0,
      startedDate: article.publishedDate // "2026-02-15"
    };
  }).filter(Boolean) : [];

  const handlePageChange = (pageNumber, e) => {
    if (e) {
      e?.preventDefault();
      e?.currentTarget?.blur();
    }

    if (topRef?.current) {
      topRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    setTimeout(() => {
      setCurrentPage(pageNumber);
    }, 300);
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)] relative">
      <Header />

      {/* Mobile Header Bar */}
      <div className="flex lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#F9F7F2]/95 backdrop-blur-sm z-50 px-4 justify-between items-center mr-0 py-[31px] mb-[-169px] pt-px pb-[25px] mt-6">
        <div className="flex items-center gap-2 mb-[-5px] mt-4 pb-3">
          <Image
            src="/assets/images/header_logo-1769143036795.png"
            alt="شعار دوائر"
            className="h-8 object-contain"
            style={{ filter: 'brightness(0) saturate(100%) invert(61%) sepia(32%) saturate(567%) hue-rotate(358deg) brightness(92%) contrast(87%)' }} />
          <div className="flex flex-col justify-center">
            <h1
              className="font-bold text-[32px] mt-0.5 mb-[5px] py-0"
              style={{ fontFamily: 'Aref Ruqaa, serif', color: '#C5A059' }}>
              دوائر
            </h1>
            <p
              className="whitespace-nowrap text-xs pt-2"
              style={{ color: '#5A5A5A' }}>
              نغرّد داخل السِرب؛ آملين أن تنبُتَ للجميع أجنحة.
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsMobileDrawerOpen(true)}
          className="flex items-center justify-center w-10 h-10"
          aria-label="القائمة">
          <Icon name="Menu" size={24} color="#C5A059" />
        </button>
      </div>

      {/* Mobile Drawer Overlay */}
      {isMobileDrawerOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setIsMobileDrawerOpen(false)}>
          <div
            className="absolute top-0 right-0 bottom-0 w-80 bg-[#F9F7F2] shadow-2xl overflow-y-auto scrollbar-hide animate-slideInRight"
            onClick={(e) => e?.stopPropagation()}>
            <div className="flex justify-end p-4">
              <button
                onClick={() => setIsMobileDrawerOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-black/5"
                aria-label="إغلاق">
                <Icon name="X" size={24} color="#5A5A5A" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4 text-center">
              <a href="/" className="block text-xl font-medium text-[#2C2B29] py-3 text-right" style={{ fontFamily: 'Amiri, serif' }}>الرئيسية</a>
              <a href="/about-dawaaer" className="block text-xl font-medium text-[#2C2B29] py-3 text-right" style={{ fontFamily: 'Amiri, serif' }}>عن دوائر</a>
              <a href="/about" className="block text-xl font-medium text-[#2C2B29] py-3 text-right" style={{ fontFamily: 'Amiri, serif' }}>من نحن؟</a>
              <a href="/policy" className="block text-xl font-medium text-[#2C2B29] py-3 text-right" style={{ fontFamily: 'Amiri, serif' }}>سياستنا</a>
            </div>

            <div className="mx-6 my-4 border-t border-[#C5A059]/30" />

            <div className="px-6 py-4">
              <h3 className="text-lg font-bold mb-4 text-right" style={{ fontFamily: 'Aref Ruqaa, serif', color: '#2C2B29' }}>
                دوائر
              </h3>
              <div className="space-y-2">
                {circles.map((circle) =>
                  <button
                    key={circle.id}
                    onClick={(e) => {
                      handleFilterChange(circle.id, e);
                      setIsMobileDrawerOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all min-h-[44px] ${activeCircle === circle.id ?
                      'bg-[#C5A059]/15 text-[#C5A059]' :
                      'text-[#2C2B29] hover:bg-[#C5A059]/10'}`
                    }
                    style={{ fontFamily: 'Amiri, serif' }}>
                    {/* Simplified Icon Rendering for Safety */}
                    <div className="flex items-center justify-center w-10 h-10 flex-shrink-0">
                      <Icon
                        name={circle.icon}
                        size={20}
                        color={activeCircle === circle.id ? '#C5A059' : circle.color}
                      />
                    </div>
                    <span className="flex-1 text-right text-base">{circle.name}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Discussion Circles Button */}
            <div className="px-6 py-4">
              <button
                onClick={() => {
                  navigate('/discussions-feed');
                  setIsMobileDrawerOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all min-h-[44px] bg-[#C5A059]/10 text-[#C5A059] hover:bg-[#C5A059]/20"
                style={{ fontFamily: 'Amiri, serif' }}>
                <div className="flex items-center justify-center w-10 h-10 flex-shrink-0">
                  <Icon name="Users" size={20} color="#C5A059" />
                </div>
                <span className="flex-1 text-right text-base">حلقات نقاشية</span>
              </button>
            </div>

            <div className="px-6 py-6 mt-auto">
              <button
                onClick={() => navigate('/creator-studio')}
                className="w-full bg-[#C5A059] text-white py-4 rounded-full font-medium text-lg min-h-[44px]"
                style={{ fontFamily: 'Amiri, serif' }}>
                اترك أثراً
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="pt-16 lg:pt-20">
        <div ref={topRef} />
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10 lg:py-12 pt-16 mt-16">
          {/* Header Section */}
          <div className="text-center mb-12 md:mb-16">
            <h1
              className="text-4xl md:text-5xl lg:text-6xl text-[var(--color-foreground)] mb-[17px]"
              style={{ fontFamily: 'Aref Ruqaa, serif', fontWeight: 700 }}>
              حلقات النقاش
            </h1>
          </div>

          {/* Discussions Grid - 3 Column Layout */}
          {loading ? (
            <div className="text-center py-20 text-gray-500">جاري التحميل...</div>
          ) : (
            <div className="grid grid-cols-2 gap-8 lg:grid-cols-3">
              {currentItems.map((discussion) => (
                <DiscussionCard
                  key={discussion.id}
                  discussion={discussion}
                  onClick={() => navigate(`/discussion/${discussion.id}`)}
                  isBookmarked={bookmarkedIds.has(discussion.id)}
                  onBookmarkToggle={handleBookmarkToggle}
                  isAuthenticated={isAuthenticated}
                />
              ))}
              {currentItems.length === 0 && (
                <div className="col-span-full text-center py-20 text-gray-500 font-bold" style={{ fontFamily: 'Amiri, serif' }}>
                  لا توجد حلقات نقاش في الوقت الحالي. كن أول من يبدأ!
                </div>
              )}
            </div>
          )}

          {/* Pagination Component */}
          {totalCount > 0 &&
            <div className="w-full flex justify-center mt-12 mb-16 relative z-20">
              <Pagination
                postsPerPage={ITEMS_PER_PAGE}
                totalPosts={totalCount}
                paginate={handlePageChange}
                currentPage={currentPage}
              />
            </div>
          }
        </div>
      </main >
    </div >
  );
};

export default DiscussionsFeed;